import { useId } from 'react'
import type { ReactElement } from 'react'
import { Box } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useLocale } from '@/contexts/LocaleContext'
import type { AppLanguage } from '@/i18n/types'

/**
 * Inline SVG flags rather than emoji flags — emoji flags render
 * inconsistently across platforms (and not at all on Windows).
 */
function PolishFlag() {
  return (
    <Box component="svg" viewBox="0 0 60 40" aria-hidden focusable="false" sx={flagSx}>
      <rect width="60" height="40" fill="#fff" />
      <rect y="20" width="60" height="20" fill="#DC143C" />
    </Box>
  )
}

function BritishFlag() {
  // useId keeps the clip path unique when several switchers share a page.
  const clip = `union-jack-${useId()}`
  return (
    <Box component="svg" viewBox="0 0 60 40" aria-hidden focusable="false" sx={flagSx}>
      <clipPath id={clip}>
        <path d="M30,20 h30 v20 z v20 h-30 z h-30 v-20 z v-20 h30 z" />
      </clipPath>
      <rect width="60" height="40" fill="#012169" />
      <path d="M0,0 L60,40 M60,0 L0,40" stroke="#fff" strokeWidth="8" />
      <path d="M0,0 L60,40 M60,0 L0,40" clipPath={`url(#${clip})`} stroke="#C8102E" strokeWidth="5" />
      <path d="M30,0 v40 M0,20 h60" stroke="#fff" strokeWidth="13" />
      <path d="M30,0 v40 M0,20 h60" stroke="#C8102E" strokeWidth="8" />
    </Box>
  )
}

const flagSx = {
  display: 'block',
  width: 22,
  height: 15,
  borderRadius: '2px',
  // Hairline keeps the white half of the Polish flag from bleeding into the page.
  boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.14)',
} as const

// `ReactElement`, not `JSX.Element`: React 19 dropped the global JSX namespace,
// so the bare name compiles nowhere and takes the whole build down with it.
const LANGUAGES: Record<AppLanguage, { Flag: () => ReactElement; nameKey: string }> = {
  pl: { Flag: PolishFlag, nameKey: 'languagePolish' },
  en: { Flag: BritishFlag, nameKey: 'languageEnglish' },
}

/**
 * A single flag button. It shows the language you would switch *to*, so the
 * click has a visible target rather than being an unlabelled action on the
 * current language.
 */
export default function LanguageSwitcher() {
  const { language, setLanguage } = useLocale()
  const { t } = useTranslation('common')

  const next: AppLanguage = language === 'pl' ? 'en' : 'pl'
  const { Flag, nameKey } = LANGUAGES[next]

  return (
    <Box
      component="button"
      type="button"
      onClick={() => void setLanguage(next)}
      title={t('switchToLanguage', { language: t(nameKey) })}
      aria-label={t('switchToLanguage', { language: t(nameKey) })}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        p: 0,
        border: 'none',
        bgcolor: 'transparent',
        cursor: 'pointer',
        borderRadius: '3px',
        lineHeight: 0,
        opacity: 0.85,
        transition: 'opacity .15s ease, transform .15s ease',
        '&:hover': { opacity: 1, transform: 'scale(1.08)' },
        '&:focus-visible': {
          outline: 'none',
          boxShadow: (theme) => theme.affexy.shadows.focus,
        },
      }}
    >
      <Flag />
    </Box>
  )
}
